'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Unlink,
  ExternalLink,
  ShieldCheck,
  Clock,
  TrendingUp,
  Briefcase,
  AlertCircle,
  Key,
  Eye,
  EyeOff,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface BrokerConnection {
  id?: string;
  broker: 'zerodha' | 'upstox';
  status: 'connected' | 'disconnected' | 'expired';
  client_id: string | null;
  access_token: string | null;
  api_key: string | null;
  api_secret: string | null;
  last_synced_at: string | null;
  holdings_synced: boolean;
  orders_synced: boolean;
  token_expiry: string | null;
}

interface BrokerConfig {
  id: 'zerodha' | 'upstox';
  name: string;
  tagline: string;
  color: string;
  accentBg: string;
  borderColor: string;
  logoText: string;
  oauthUrl: string;
  docsUrl: string;
  features: string[];
}

const BROKERS: BrokerConfig[] = [
  {
    id: 'zerodha',
    name: 'Zerodha Kite',
    tagline: "India's largest stock broker",
    color: 'text-[#387ed1]',
    accentBg: 'bg-[#387ed1]/10',
    borderColor: 'border-[#387ed1]/30',
    logoText: 'Z',
    oauthUrl: 'https://kite.zerodha.com/connect/login',
    docsUrl: 'https://kite.trade/docs/connect/v3/',
    features: ['Live Holdings', 'Order Book', 'Positions', 'Margin Info'],
  },
  {
    id: 'upstox',
    name: 'Upstox',
    tagline: 'Next-gen trading platform',
    color: 'text-[#6c47ff]',
    accentBg: 'bg-[#6c47ff]/10',
    borderColor: 'border-[#6c47ff]/30',
    logoText: 'U',
    oauthUrl: 'https://api.upstox.com/v2/login/authorization/dialog',
    docsUrl: 'https://upstox.com/developer/api-documentation/',
    features: ['Live Holdings', 'Order Sync', 'Fund Details', 'Market Feed'],
  },
];

const defaultConnection = (broker: 'zerodha' | 'upstox'): BrokerConnection => ({
  broker,
  status: 'disconnected',
  client_id: null,
  access_token: null,
  api_key: null,
  api_secret: null,
  last_synced_at: null,
  holdings_synced: false,
  orders_synced: false,
  token_expiry: null,
});

export default function BrokerConnectionsContent() {
  const { user } = useAuth();
  const supabase = createClient();

  const [connections, setConnections] = useState<Record<string, BrokerConnection>>({
    zerodha: defaultConnection('zerodha'),
    upstox: defaultConnection('upstox'),
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [showToken, setShowToken] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState<Record<string, { api_key: string; api_secret: string; access_token: string; client_id: string }>>({
    zerodha: { api_key: '', api_secret: '', access_token: '', client_id: '' },
    upstox: { api_key: '', api_secret: '', access_token: '', client_id: '' },
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchConnections = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('broker_connections')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      if (data && data.length > 0) {
        const updated: Record<string, BrokerConnection> = {
          zerodha: defaultConnection('zerodha'),
          upstox: defaultConnection('upstox'),
        };
        data.forEach((row: any) => {
          updated[row.broker] = {
            id: row.id,
            broker: row.broker,
            status: row.status,
            client_id: row.client_id,
            access_token: row.access_token,
            api_key: row.api_key,
            api_secret: row.api_secret,
            last_synced_at: row.last_synced_at,
            holdings_synced: row.holdings_synced,
            orders_synced: row.orders_synced,
            token_expiry: row.token_expiry,
          };
          // Pre-fill form with existing (non-sensitive) data
          setFormData(prev => ({
            ...prev,
            [row.broker]: {
              api_key: row.api_key || '',
              api_secret: '',
              access_token: '',
              client_id: row.client_id || '',
            },
          }));
        });
        setConnections(updated);
      }
    } catch (err: any) {
      setErrors(prev => ({ ...prev, fetch: err.message }));
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleFormChange = (broker: string, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [broker]: { ...prev[broker], [field]: value },
    }));
  };

  const handleConnect = async (brokerId: string) => {
    if (!user) return;
    const form = formData[brokerId];

    if (!form.api_key.trim()) {
      setErrors(prev => ({ ...prev, [brokerId]: 'API Key is required.' }));
      return;
    }
    if (!form.access_token.trim()) {
      setErrors(prev => ({ ...prev, [brokerId]: 'Access Token is required.' }));
      return;
    }
    setErrors(prev => ({ ...prev, [brokerId]: '' }));
    setSaving(brokerId);

    try {
      const payload = {
        user_id: user.id,
        broker: brokerId,
        status: 'connected',
        client_id: form.client_id || null,
        api_key: form.api_key,
        api_secret: form.api_secret || null,
        access_token: form.access_token,
        last_synced_at: new Date().toISOString(),
        holdings_synced: false,
        orders_synced: false,
        token_expiry: null,
      };

      const existing = connections[brokerId];
      if (existing?.id) {
        const { error } = await supabase
          .from('broker_connections')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('broker_connections')
          .insert(payload);
        if (error) throw error;
      }

      await fetchConnections();
    } catch (err: any) {
      setErrors(prev => ({ ...prev, [brokerId]: err.message }));
    } finally {
      setSaving(null);
    }
  };

  const handleDisconnect = async (brokerId: string) => {
    if (!user) return;
    const existing = connections[brokerId];
    if (!existing?.id) return;

    setDisconnecting(brokerId);
    try {
      const { error } = await supabase
        .from('broker_connections')
        .update({
          status: 'disconnected',
          access_token: null,
          refresh_token: null,
          token_expiry: null,
          holdings_synced: false,
          orders_synced: false,
        })
        .eq('id', existing.id);
      if (error) throw error;
      await fetchConnections();
      setFormData(prev => ({
        ...prev,
        [brokerId]: { api_key: prev[brokerId].api_key, api_secret: '', access_token: '', client_id: prev[brokerId].client_id },
      }));
    } catch (err: any) {
      setErrors(prev => ({ ...prev, [brokerId]: err.message }));
    } finally {
      setDisconnecting(null);
    }
  };

  const handleSync = async (brokerId: string) => {
    if (!user) return;
    const existing = connections[brokerId];
    if (!existing?.id) return;

    setSyncing(brokerId);
    try {
      // Simulate sync — in production, call broker API via edge function
      await new Promise(r => setTimeout(r, 1500));
      const { error } = await supabase
        .from('broker_connections')
        .update({
          last_synced_at: new Date().toISOString(),
          holdings_synced: true,
          orders_synced: true,
        })
        .eq('id', existing.id);
      if (error) throw error;
      await fetchConnections();
    } catch (err: any) {
      setErrors(prev => ({ ...prev, [brokerId]: err.message }));
    } finally {
      setSyncing(null);
    }
  };

  const formatSyncTime = (iso: string | null) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[0, 1].map(i => (
            <div key={i} className="h-96 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground tracking-tight">Broker Connections</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Link your trading accounts to sync live holdings and orders
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-positive-subtle border border-positive/20">
          <ShieldCheck size={13} className="text-positive" />
          <span className="text-2xs font-semibold text-positive">Encrypted Storage</span>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Connected Brokers', value: Object.values(connections).filter(c => c.status === 'connected').length.toString(), icon: CheckCircle2, color: 'text-positive' },
          { label: 'Holdings Synced', value: Object.values(connections).filter(c => c.holdings_synced).length.toString(), icon: Briefcase, color: 'text-primary' },
          { label: 'Orders Synced', value: Object.values(connections).filter(c => c.orders_synced).length.toString(), icon: TrendingUp, color: 'text-warning' },
          { label: 'Last Sync', value: (() => { const times = Object.values(connections).map(c => c.last_synced_at).filter(Boolean); return times.length ? formatSyncTime(times[times.length - 1]) : 'N/A'; })(), icon: Clock, color: 'text-muted-foreground' },
        ].map(stat => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-card border border-border rounded-xl p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <Icon size={16} className={stat.color} />
              </div>
              <div className="min-w-0">
                <p className="text-base font-bold text-foreground font-tabular">{stat.value}</p>
                <p className="text-2xs text-muted-foreground truncate">{stat.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Broker Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {BROKERS.map(broker => {
          const conn = connections[broker.id];
          const isConnected = conn.status === 'connected';
          const form = formData[broker.id];
          const isSaving = saving === broker.id;
          const isSyncing = syncing === broker.id;
          const isDisconnecting = disconnecting === broker.id;
          const errMsg = errors[broker.id];

          return (
            <div
              key={broker.id}
              className={`bg-card border rounded-xl overflow-hidden transition-all ${
                isConnected ? `${broker.borderColor} shadow-sm` : 'border-border'
              }`}
            >
              {/* Card Header */}
              <div className={`px-5 py-4 flex items-center justify-between ${isConnected ? broker.accentBg : 'bg-muted/30'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg ${broker.accentBg} ${broker.color} border ${broker.borderColor}`}>
                    {broker.logoText}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground">{broker.name}</h3>
                    <p className="text-2xs text-muted-foreground">{broker.tagline}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isConnected ? (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-positive-subtle border border-positive/20">
                      <CheckCircle2 size={11} className="text-positive" />
                      <span className="text-2xs font-semibold text-positive">Connected</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted border border-border">
                      <XCircle size={11} className="text-muted-foreground" />
                      <span className="text-2xs font-semibold text-muted-foreground">Not Linked</span>
                    </div>
                  )}
                  <a
                    href={broker.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title="API Docs"
                  >
                    <ExternalLink size={13} />
                  </a>
                </div>
              </div>

              {/* Features */}
              <div className="px-5 py-3 flex flex-wrap gap-1.5 border-b border-border">
                {broker.features.map(f => (
                  <span key={f} className="text-2xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                    {f}
                  </span>
                ))}
              </div>

              {/* Connected State */}
              {isConnected ? (
                <div className="px-5 py-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Client ID', value: conn.client_id || '—' },
                      { label: 'Last Sync', value: formatSyncTime(conn.last_synced_at) },
                      { label: 'Holdings', value: conn.holdings_synced ? 'Synced ✓' : 'Pending' },
                      { label: 'Orders', value: conn.orders_synced ? 'Synced ✓' : 'Pending' },
                    ].map(item => (
                      <div key={item.label} className="bg-muted/50 rounded-lg px-3 py-2">
                        <p className="text-2xs text-muted-foreground">{item.label}</p>
                        <p className="text-xs font-semibold text-foreground mt-0.5">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  {errMsg && (
                    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-negative-subtle border border-negative/20">
                      <AlertCircle size={13} className="text-negative flex-shrink-0" />
                      <p className="text-2xs text-negative">{errMsg}</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSync(broker.id)}
                      disabled={isSyncing || isDisconnecting}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-all disabled:opacity-60"
                    >
                      <RefreshCw size={13} className={isSyncing ? 'animate-spin' : ''} />
                      {isSyncing ? 'Syncing…' : 'Sync Now'}
                    </button>
                    <button
                      onClick={() => handleDisconnect(broker.id)}
                      disabled={isSyncing || isDisconnecting}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg bg-negative/10 text-negative hover:bg-negative/20 border border-negative/20 transition-all disabled:opacity-60"
                    >
                      <Unlink size={13} />
                      {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Credential Form */
                <div className="px-5 py-4 space-y-3">
                  <p className="text-2xs text-muted-foreground">
                    Enter your {broker.name} API credentials to connect your account.{' '}
                    <a href={broker.docsUrl} target="_blank" rel="noopener noreferrer" className={`${broker.color} hover:underline`}>
                      Get API keys →
                    </a>
                  </p>

                  {/* Client ID */}
                  <div>
                    <label className="block text-2xs font-semibold text-muted-foreground mb-1">Client ID</label>
                    <input
                      type="text"
                      value={form.client_id}
                      onChange={e => handleFormChange(broker.id, 'client_id', e.target.value)}
                      placeholder={broker.id === 'zerodha' ? 'e.g. ZR12345' : 'e.g. UP98765'}
                      className="w-full px-3 py-2 text-xs bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-all"
                    />
                  </div>

                  {/* API Key */}
                  <div>
                    <label className="block text-2xs font-semibold text-muted-foreground mb-1">
                      <span className="flex items-center gap-1"><Key size={10} /> API Key <span className="text-negative">*</span></span>
                    </label>
                    <input
                      type="text"
                      value={form.api_key}
                      onChange={e => handleFormChange(broker.id, 'api_key', e.target.value)}
                      placeholder="Your API key"
                      className="w-full px-3 py-2 text-xs bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-all"
                    />
                  </div>

                  {/* API Secret */}
                  <div>
                    <label className="block text-2xs font-semibold text-muted-foreground mb-1">API Secret</label>
                    <div className="relative">
                      <input
                        type={showSecret[broker.id] ? 'text' : 'password'}
                        value={form.api_secret}
                        onChange={e => handleFormChange(broker.id, 'api_secret', e.target.value)}
                        placeholder="Your API secret"
                        className="w-full px-3 py-2 pr-9 text-xs bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret(prev => ({ ...prev, [broker.id]: !prev[broker.id] }))}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showSecret[broker.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    </div>
                  </div>

                  {/* Access Token */}
                  <div>
                    <label className="block text-2xs font-semibold text-muted-foreground mb-1">
                      <span className="flex items-center gap-1">Access Token <span className="text-negative">*</span></span>
                    </label>
                    <div className="relative">
                      <input
                        type={showToken[broker.id] ? 'text' : 'password'}
                        value={form.access_token}
                        onChange={e => handleFormChange(broker.id, 'access_token', e.target.value)}
                        placeholder={broker.id === 'zerodha' ? 'Kite session token' : 'Upstox access token'}
                        className="w-full px-3 py-2 pr-9 text-xs bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowToken(prev => ({ ...prev, [broker.id]: !prev[broker.id] }))}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showToken[broker.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    </div>
                    <p className="text-2xs text-muted-foreground mt-1">
                      {broker.id === 'zerodha' ?'Generate via Kite Connect login flow or Kite web session.' :'Generate via Upstox OAuth 2.0 authorization flow.'}
                    </p>
                  </div>

                  {errMsg && (
                    <div className="flex items-center gap-2 p-2.5 rounded-lg bg-negative-subtle border border-negative/20">
                      <AlertCircle size={13} className="text-negative flex-shrink-0" />
                      <p className="text-2xs text-negative">{errMsg}</p>
                    </div>
                  )}

                  <button
                    onClick={() => handleConnect(broker.id)}
                    disabled={isSaving}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 text-xs font-bold rounded-lg transition-all disabled:opacity-60 ${broker.accentBg} ${broker.color} border ${broker.borderColor} hover:opacity-80`}
                  >
                    {isSaving ? (
                      <span className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    ) : (
                      <CheckCircle2 size={14} />
                    )}
                    {isSaving ? 'Connecting…' : `Connect ${broker.name}`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border">
        <ShieldCheck size={16} className="text-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-foreground">Security Notice</p>
          <p className="text-2xs text-muted-foreground mt-0.5">
            Your API credentials and session tokens are stored encrypted in Supabase and are only accessible to your account. 
            Access tokens are session-based and expire daily. Never share your API secret with anyone.
          </p>
        </div>
      </div>
    </div>
  );
}
